import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Text, Spinner, Center } from "@chakra-ui/react";
import { useCourseDetails } from "../hooks/useSkillDevelopment";
import CourseDetailsHero from "../components/CareersComponents/SkillDevelopment/CourseDetailsHero";
import CourseDetailsMainInfo from "../components/CareersComponents/SkillDevelopment/CourseDetailsMainInfo";
import WhatYouWillLearn from "../components/CareersComponents/SkillDevelopment/WhatYouWillLearn";
import CareersFooter from "../components/CareersComponents/CareersFooter";

const CourseDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  
  // Try to get course from navigation state first
  const stateCourse = location.state?.course;
  
  // Extract course ID - use the URL parameter (which should be training_id)
  const courseId = id; // This will be the training_id from the URL
  
  // Always try to fetch from API if we have a courseId
  // This ensures we get the latest data from the API using the training_id
  const shouldFetchFromAPI = !!courseId;
  
  // Log the course ID being used for API call
  // Fetch course details from API (pass training_id)
  const { courseDetails, loading, error } = useCourseDetails(shouldFetchFromAPI ? courseId : null);
  
  // Prioritize API data, fall back to state course data
  let course = null;
  
  // First, try to use API data if available
  if (courseDetails) {
    const details = courseDetails.courseDetails || courseDetails;
    const speakers = courseDetails.speakers || [];
    
    // Transform API data to match component expectations
    course = {
      // Use training_id as the primary identifier
      id: details.training_id,
      training_id: details.training_id,
      encodeId: details.encode_id,
      encode_id: details.encode_id,
      title: details.training_name || details.name || details.course_name,
      image: details.course_thumbnail,
      tag: "Training",
      price: details.batch_type === "paid" ? `${details.amount || details.fee || 0} BDT` : "- -",
      batch_type: details.batch_type === "paid" ? "Premium" : details.batch_type === "free" ? "Free" : details.batch_type,
      classes: details.total_session || "TBA",
      duration: details.total_session_duration || details.duration || "TBA",
      enrolled: details.seat_capacity || "TBA",
      deadline: details.enrollment_deadline || details.deadline,
      description: details.training_overview || details.description || "",
      objective: details.training_objective || details.objective || "",
      outline: details.training_outline || details.outline || "",
      qualification: details.course_qualification || details.qualification || "",
      venue: details.venue_name || details.location,
      startDate: details.start_date,
      endDate: details.end_date,
      startTime: details.start_time,
      endTime: details.end_time,
      status: details.batch_status || details.status,
      sessionDays: details.session_day ? JSON.parse(details.session_day) : [],
      seatCapacity: details.seat_capacity,
      eachClassDuration: details.each_class_duration,
      expectedStartingDate: details.expected_starting_date,
      speakers: speakers
    };
  }
  
  // If no API data, fall back to state course data and keep HTML for markdown fields
  if (!course && stateCourse) {
    course = {
      ...stateCourse,
      // Keep HTML for all fields so MarkdownRenderer can handle them
    };
  }

    // Use API data for all display values
    const description = course?.description || course?.qualification || "";
    const rating = "4.6"; // Default rating since API doesn't provide this
    const reviews = "N/A"; // Default since API doesn't provide this
    const employees = course?.seatCapacity || "TBA";

  // Show loading state
  if (loading) {
    return (
      <Center h="50vh">
        <Spinner size="xl" color="#235B63" />
      </Center>
    );
  }

  // Show error or not found state
  if (error || !course) {
    return (
      <Box p={10} textAlign="center">
        <Text fontSize="xl" color="red.500">
          {error || "Course not found."}
        </Text>
      </Box>
    );
  }

  return (
    <>
      <CourseDetailsHero />
      <Box px={{ base: 2, md: 10, lg: 32 }} w="100%" maxW="1600px" mx="auto">
        <CourseDetailsMainInfo
          image={course.image}
          title={course.title}
          description={description}
          tag={course.tag}
          price={course.price}
          batch_type={course.batch_type}
          classes={course.classes}
          duration={course.duration}
          enrolled={course.enrolled}
          deadline={course.deadline}
          rating={rating}
          reviews={reviews}
          encodeId={course.encode_id}
          employees={employees}
          venue={course.venue}
          startDate={course.startDate}
          endDate={course.endDate}
          startTime={course.startTime}
          endTime={course.endTime}
          status={course.status}
          sessionDays={course.sessionDays}
          seatCapacity={course.seatCapacity}
          eachClassDuration={course.eachClassDuration}
          expectedStartingDate={course.expectedStartingDate}
          speakers={course.speakers}
          courseId={course.training_id}
          batchName={course.title}
          trainingName={course.title}
          courseThumbnail={course.image}
          batchStatus={course.status}
        />
        <WhatYouWillLearn 
          objective={course.objective}
          outline={course.outline}
          qualification={course.qualification}
          description={course.description}
        />
      </Box>
      
      {/* <Text fontSize="3xl" fontWeight="bold" mb={6} color="#23463B" position="relative" zIndex={2} pt={4} textAlign="center">
        Success Story
      </Text>
      <CourseGallery />
      <JoinUsTeamActivity /> */}
      <CareersFooter />
    </>
  );
};

export default CourseDetails;